import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

type Option = { value: string; label: string };
type ChoiceProps = Omit<ComponentPropsWithoutRef<typeof SelectTrigger>, "value" | "onChange"> & {
  value?: string; onValueChange: (value: string) => void; options: readonly Option[]; placeholder?: string;
};
const emptyChoice = "__io_spoke_empty_choice__";

/** Empty choices round-trip without using Radix's reserved empty item value. */
export const ChoiceSelect = forwardRef<HTMLButtonElement, ChoiceProps>(function ChoiceSelect({ value, onValueChange, options, placeholder, disabled, name, ...trigger }, ref) {
  const hasEmptyOption = options.some((option) => option.value === "");
  const selected = value === "" && hasEmptyOption ? emptyChoice : value ?? "";
  return <Select name={name} value={selected} onValueChange={(next) => onValueChange(next === emptyChoice ? "" : next)} disabled={disabled}>
    <SelectTrigger {...trigger} ref={ref}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value || emptyChoice}>{option.label}</SelectItem>)}</SelectContent>
  </Select>;
});

/** Preserve RHF validation, blur tracking, reset/defaults and visible invalid-field focus. */
export function FormChoiceSelect<T extends FieldValues, Output = T>({ control, name, ...props }: Omit<ChoiceProps, "value" | "onValueChange" | "name"> & { control: Control<T, unknown, Output>; name: FieldPath<T> }) {
  return <Controller control={control} name={name} render={({ field, fieldState }) => <ChoiceSelect {...props} name={field.name} ref={field.ref} onBlur={field.onBlur} value={field.value == null ? "" : String(field.value)} onValueChange={field.onChange} aria-invalid={props["aria-invalid"] ?? fieldState.invalid} />} />;
}
